<template>
  <CommonLayout selectedNav="home">
    <div class="tx-wrap">

      <!-- Hero -->
      <div class="tx-hero">
        <h1 class="tx-hero-title">在线文件<em>转换器</em></h1>
        <p class="tx-hero-sub">EXCEL · PDF · WORD · HTML &nbsp;—&nbsp; 多格式互转，高保真还原版式与样式</p>
        <p class="tx-privacy"><i class="el-icon-lock"></i> 隐私保护：上传文件与转换结果将在 7 天后自动删除</p>
      </div>

      <!-- 识别提示条 -->
      <transition name="tx-slide">
        <div class="tx-detect-bar" v-if="detectTips.length">
          <i class="el-icon-warning-outline"></i>
          <span>格式识别提示：{{ detectTips.join(' ；') }}</span>
          <i class="el-icon-close tx-detect-close" @click="detectTips = []"></i>
        </div>
      </transition>

      <!-- 转换面板 -->
      <div class="tx-panel">
        <span class="tx-corner tl"></span><span class="tx-corner tr"></span>
        <span class="tx-corner bl"></span><span class="tx-corner br"></span>

        <el-row :gutter="24" type="flex" align="top" class="tx-panel-row">
          <el-col :xs="24" :sm="24" :md="14" class="tx-col">
            <el-upload class="tx-upload" drag show-file-list :on-preview="handlePreview"
              :on-remove="handleRemove" :before-remove="beforeRemove" :on-exceed="handleExceed"
              :before-upload="beforeUpload"
              :on-success="uploadSuccess" :action="uploadUrl" multiple :limit="maxUploadCount">
              <div class="tx-upload-inner">
                <i class="el-icon-upload2 tx-upload-icon"></i>
                <div class="tx-upload-text">将文件拖到此处，或 <em>点击上传</em></div>
                <div class="tx-upload-tip">支持批量上传 · {{ uploadLimitTip }}</div>
              </div>
            </el-upload>
            <div class="tx-source" v-if="fileListData.length">
              <span class="tx-field-label">// 识别源格式 SOURCE</span>
              <el-tag size="medium" class="tx-source-tag" effect="dark">{{ sourceExt || '未知' }}</el-tag>
              <span class="tx-source-count">{{ fileListData.length }} 个文件待转换</span>
            </div>
          </el-col>

          <el-col :xs="24" :sm="24" :md="10" class="tx-col">
            <div class="tx-controls">
              <div class="tx-field-label">// 目标格式 TARGET FORMAT</div>
              <el-select v-model="formatValue" clearable placeholder="选择转换目标格式" class="tx-select"
                :loading="capLoading" @change="onTargetChange">
                <el-option v-for="opt in options" :key="opt.value" :value="opt.value" :label="opt.label">
                  <span class="tx-opt-label">{{ opt.label }}</span>
                  <span class="tx-loss" :class="'tx-loss-' + opt.loss">{{ opt.lossLabel }}</span>
                </el-option>
                <template slot="empty">
                  <div class="tx-opt-empty">无可转换目标格式</div>
                </template>
              </el-select>

              <div class="tx-loss-legend" v-if="options.length">
                <span class="tx-loss tx-loss-L">L 无损</span>
                <span class="tx-loss tx-loss-Y">Y 有损</span>
                <span class="tx-loss tx-loss-F">F 版式固化</span>
                <span class="tx-loss tx-loss-X">X 维度跨越</span>
              </div>

              <div class="tx-unsupported" v-if="unsupportedTip">
                <i class="el-icon-info"></i> {{ unsupportedTip }}
              </div>

              <el-button type="primary" class="tx-btn" :loading="converting" :disabled="!options.length"
                @click="onConvertClick">
                {{ converting ? '转换中...' : '开始转换' }}
                <i v-if="!converting" class="el-icon-right tx-btn-arrow"></i>
              </el-button>

              <div class="tx-pipeline">PIPELINE&nbsp;&nbsp;读取 → 转换 → 输出</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 转换结果 / 任务 -->
      <div class="tx-result" v-if="resultTasks.length">
        <div class="tx-result-head">
          <span class="tx-result-title"><i class="el-icon-data-line"></i> 转换结果</span>
          <span class="tx-result-count">{{ resultTasks.length }} FILES DONE</span>
        </div>

        <el-table :data="resultTasks" height="260" class="tx-table" style="width: 100%">
          <el-table-column prop="fileName" label="文件名" min-width="220" show-overflow-tooltip>
          </el-table-column>
          <el-table-column label="进度" min-width="160">
            <template slot-scope="scope">
              <el-progress :percentage="scope.row.progress" :status="progressStatus(scope.row)"
                :stroke-width="8" :text-inside="false" class="tx-progress"></el-progress>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110">
            <template slot-scope="scope">
              <span class="tx-badge" :class="'tx-badge-' + scope.row.status">{{ statusText(scope.row.status) }}</span>
              <i v-if="scope.row.note" class="el-icon-warning-outline tx-note" :title="scope.row.note"></i>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="170">
            <template slot-scope="scope">
              <el-link v-if="scope.row.result" type="success" :underline="false" class="tx-dl"
                @click="downloadResult(scope.row)">
                <i class="el-icon-download"></i> 下载
              </el-link>
              <el-link v-if="scope.row.status === 'failed'" type="warning" :underline="false" class="tx-retry"
                @click="retryTask(scope.row)">
                <i class="el-icon-refresh"></i> 重试
              </el-link>
              <el-tooltip v-if="scope.row.hint || scope.row.error" :content="scope.row.hint || scope.row.error"
                placement="top">
                <i class="el-icon-question tx-help"></i>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>

        <div class="tx-result-foot">
          <router-link to="/tasks" class="tx-link">前往任务中心 →</router-link>
        </div>
      </div>

      <!-- 空状态 -->
      <div class="tx-empty" v-else>
        <i class="el-icon-cpu"></i>
        <p>暂无转换记录 — 上传文件并选择目标格式后点击「开始转换」</p>
      </div>

      <!-- X 类二次确认 -->
      <el-dialog title="转换前确认" :visible.sync="xConfirmVisible" width="460px" class="tx-dialog" :append-to-body="true">
        <div class="tx-x-confirm">
          <i class="el-icon-warning tx-x-icon"></i>
          <p class="tx-x-text">{{ xConfirmText }}</p>
          <p class="tx-x-sub">目标格式：<b>{{ formatValue }}</b> ｜ 源格式：<b>{{ sourceExt }}</b></p>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="xConfirmVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmConvert">仍要转换</el-button>
        </span>
      </el-dialog>

    </div>
  </CommonLayout>
</template>

<style>
/* ================= 页面容器 ================= */
.tx-wrap {
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px 28px 60px;
  animation: txFadeUp .5s ease both;
}

@keyframes txFadeUp {
  from {
    opacity: 0;
    transform: translateY(14px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ================= Hero ================= */
.tx-hero {
  text-align: center;
  margin-bottom: 26px;
}

.tx-hero-title {
  margin: 0 0 12px;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: 6px;
  color: #e2f5ff;
}

.tx-hero-title em {
  font-style: normal;
  background: linear-gradient(90deg, #22d3ee, #818cf8, #22d3ee);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: txShine 4s linear infinite;
}

@keyframes txShine {
  to {
    background-position: 200% center;
  }
}

.tx-hero-sub {
  margin: 0;
  color: #5b7295;
  font-size: 13px;
  letter-spacing: 2px;
}

/* ================= 识别提示条 ================= */
.tx-detect-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding: 11px 16px;
  border-radius: 10px;
  background: rgba(251, 191, 36, .1);
  border: 1px solid rgba(251, 191, 36, .35);
  color: #fcd34d;
  font-size: 13px;
  letter-spacing: .5px;
}

.tx-detect-bar i.el-icon-warning-outline {
  font-size: 18px;
  flex-shrink: 0;
}

.tx-detect-close {
  margin-left: auto;
  cursor: pointer;
  opacity: .7;
}

.tx-detect-close:hover {
  opacity: 1;
}

.tx-slide-enter-active,
.tx-slide-leave-active {
  transition: all .3s ease;
}

.tx-slide-enter,
.tx-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ================= 玻璃面板 + 四角标 ================= */
.tx-panel {
  position: relative;
  padding: 28px;
  border-radius: 16px;
  background: rgba(13, 20, 36, .72);
  border: 1px solid rgba(56, 189, 248, .22);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, .45), inset 0 0 0 1px rgba(56, 189, 248, .05);
}

.tx-panel-row {
  flex-wrap: wrap;
}

.tx-col {
  margin-bottom: 8px;
}

.tx-corner {
  position: absolute;
  width: 16px;
  height: 16px;
  border-color: #22d3ee;
  border-style: solid;
  border-width: 0;
  filter: drop-shadow(0 0 4px rgba(34, 211, 238, .8));
}

.tx-corner.tl {
  top: -1px;
  left: -1px;
  border-top-width: 2px;
  border-left-width: 2px;
}

.tx-corner.tr {
  top: -1px;
  right: -1px;
  border-top-width: 2px;
  border-right-width: 2px;
}

.tx-corner.bl {
  bottom: -1px;
  left: -1px;
  border-bottom-width: 2px;
  border-left-width: 2px;
}

.tx-corner.br {
  bottom: -1px;
  right: -1px;
  border-bottom-width: 2px;
  border-right-width: 2px;
}

/* ================= 上传区 ================= */
.tx-upload .el-upload {
  width: 100%;
}

.tx-upload .el-upload-dragger {
  width: 100%;
  height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(7, 12, 24, .5);
  border: 1px dashed rgba(34, 211, 238, .4);
  border-radius: 12px;
  transition: all .25s;
}

.tx-upload .el-upload-dragger:hover {
  border-color: #22d3ee;
  background: rgba(34, 211, 238, .05);
  box-shadow: inset 0 0 40px rgba(34, 211, 238, .08), 0 0 24px rgba(34, 211, 238, .18);
}

.tx-upload-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.tx-upload-icon {
  font-size: 52px;
  color: #22d3ee;
  text-shadow: 0 0 22px rgba(34, 211, 238, .8);
  transition: transform .25s;
}

.tx-upload .el-upload-dragger:hover .tx-upload-icon {
  transform: translateY(-4px);
}

.tx-upload-text {
  color: #c7d7ee;
  font-size: 15px;
}

.tx-upload-text em {
  color: #22d3ee;
  font-style: normal;
}

.tx-upload-tip {
  color: #5b7295;
  font-size: 12px;
  letter-spacing: 1px;
}

.tx-upload .el-upload-list__item {
  background: rgba(56, 189, 248, .06);
  border: 1px solid rgba(56, 189, 248, .18);
  color: #c7d7ee;
  border-radius: 8px;
  transition: all .2s;
}

.tx-upload .el-upload-list__item:hover {
  background: rgba(56, 189, 248, .12);
  color: #e2f5ff;
}

.tx-upload .el-upload-list__item .el-icon-upload-success {
  color: #34d399;
}

.tx-upload .el-upload-list__item-name {
  color: inherit;
}

/* 源格式显示 */
.tx-source {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.tx-source-tag {
  background: rgba(34, 211, 238, .16) !important;
  border: 1px solid rgba(34, 211, 238, .4) !important;
  color: #a5f3fc !important;
  letter-spacing: 1px;
}

.tx-source-count {
  color: #5b7295;
  font-size: 12px;
}

/* ================= 右侧控制区 ================= */
.tx-controls {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 6px 0 0 8px;
}

.tx-field-label {
  color: #22d3ee;
  font-size: 12px;
  letter-spacing: 2px;
  opacity: .85;
}

.tx-select .el-input__inner {
  background: rgba(7, 12, 24, .6);
  border: 1px solid rgba(56, 189, 248, .3);
  color: #e2f5ff;
  height: 48px;
  line-height: 48px;
  border-radius: 10px;
  letter-spacing: 1px;
  transition: all .2s;
}

.tx-select .el-input__inner:hover,
.tx-select .el-input__inner:focus {
  border-color: #22d3ee;
  box-shadow: 0 0 0 2px rgba(34, 211, 238, .12);
}

.tx-select .el-input__icon {
  color: #5b7295;
  line-height: 48px;
}

/* 下拉面板深色 */
.el-select-dropdown {
  background: #0d1424 !important;
  border: 1px solid rgba(56, 189, 248, .3) !important;
}

.el-select-dropdown__item {
  color: #c7d7ee !important;
  height: 38px;
  line-height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover {
  background: rgba(56, 189, 248, .12) !important;
}

.el-select-dropdown__item.selected {
  color: #22d3ee !important;
}

.tx-opt-label {
  letter-spacing: 1px;
}

.tx-opt-empty {
  color: #5b7295;
  padding: 8px 12px;
  font-size: 13px;
}

/* ================= 损耗标签徽章 ================= */
.tx-loss {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: .5px;
  font-weight: 600;
  border: 1px solid transparent;
  white-space: nowrap;
}

.tx-loss-L {
  color: #34d399;
  background: rgba(52, 211, 153, .12);
  border-color: rgba(52, 211, 153, .4);
}

.tx-loss-Y {
  color: #fbbf24;
  background: rgba(251, 191, 36, .12);
  border-color: rgba(251, 191, 36, .4);
}

.tx-loss-F {
  color: #38bdf8;
  background: rgba(56, 189, 248, .12);
  border-color: rgba(56, 189, 248, .4);
}

.tx-loss-X {
  color: #c084fc;
  background: rgba(192, 132, 252, .14);
  border-color: rgba(192, 132, 252, .5);
}

.tx-loss-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: -4px;
}

.tx-unsupported {
  color: #fca5a5;
  font-size: 12.5px;
  background: rgba(248, 113, 113, .08);
  border: 1px solid rgba(248, 113, 113, .25);
  border-radius: 8px;
  padding: 8px 12px;
  letter-spacing: .5px;
}

/* 渐变发光按钮 */
.tx-btn {
  height: 48px;
  border: none !important;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 4px;
  color: #04121c !important;
  background: linear-gradient(90deg, #22d3ee, #4f7cff 55%, #818cf8) !important;
  box-shadow: 0 6px 22px rgba(34, 211, 238, .35);
  transition: all .25s;
}

.tx-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 30px rgba(34, 211, 238, .5);
  filter: brightness(1.08);
}

.tx-btn.is-loading {
  opacity: .8;
}

.tx-btn:disabled {
  opacity: .45;
  cursor: not-allowed;
}

.tx-btn-arrow {
  margin-left: 6px;
}

.tx-pipeline {
  color: #5b7295;
  font-size: 11px;
  letter-spacing: 1.5px;
  font-family: Consolas, Monaco, monospace;
}

/* ================= 结果表格 ================= */
.tx-result {
  margin-top: 28px;
  border-radius: 14px;
  background: rgba(13, 20, 36, .72);
  border: 1px solid rgba(56, 189, 248, .18);
  backdrop-filter: blur(12px);
  padding: 18px 18px 8px;
}

.tx-result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 4px 14px;
}

.tx-result-title {
  color: #e2f5ff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
}

.tx-result-title i {
  color: #22d3ee;
  margin-right: 4px;
}

.tx-result-count {
  color: #34d399;
  font-size: 11px;
  letter-spacing: 2px;
  font-family: Consolas, Monaco, monospace;
}

.tx-table {
  background: transparent !important;
}

.tx-table::before,
.tx-table .el-table__inner-wrapper::before {
  display: none;
}

.tx-table th {
  background: rgba(56, 189, 248, .1) !important;
  color: #8fd8ee !important;
  border-bottom: 1px solid rgba(56, 189, 248, .25) !important;
  font-weight: 600;
  letter-spacing: 1px;
}

.tx-table tr {
  background: transparent !important;
}

.tx-table td {
  border-bottom: 1px solid rgba(56, 189, 248, .1) !important;
  color: #c7d7ee;
}

.tx-table .el-table__body tr:hover>td {
  background: rgba(56, 189, 248, .08) !important;
}

.tx-table .el-table__body-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.tx-table .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: rgba(56, 189, 248, .25);
  border-radius: 4px;
}

.tx-table--border,
.tx-table--border::after {
  border-color: rgba(56, 189, 248, .15) !important;
}

.tx-progress {
  margin: 0;
}

.tx-progress .el-progress__text {
  color: #8fd8ee;
  font-size: 12px;
}

.tx-dl {
  color: #34d399 !important;
  font-weight: 600;
}

.tx-dl i {
  margin-right: 3px;
}

.tx-dl:hover {
  color: #6ee7b7 !important;
}

.tx-retry {
  color: #fbbf24 !important;
  font-weight: 600;
  margin-left: 10px;
}

.tx-retry:hover {
  color: #fcd34d !important;
}

.tx-help {
  color: #5b7295;
  margin-left: 8px;
  cursor: help;
}

.tx-help:hover {
  color: #22d3ee;
}

.tx-result-foot {
  text-align: right;
  padding: 6px 4px 8px;
}

.tx-link {
  color: #22d3ee;
  font-size: 13px;
  text-decoration: none;
  letter-spacing: 1px;
}

.tx-link:hover {
  color: #67e8f9;
}

/* 状态徽章 */
.tx-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .5px;
}

.tx-badge-queued {
  color: #94a3b8;
  background: rgba(148, 163, 184, .14);
}

.tx-badge-running {
  color: #38bdf8;
  background: rgba(56, 189, 248, .14);
}

.tx-badge-success {
  color: #34d399;
  background: rgba(52, 211, 153, .14);
}

.tx-badge-failed {
  color: #f87171;
  background: rgba(248, 113, 113, .14);
}

/* ================= 空状态 ================= */
.tx-empty {
  margin-top: 28px;
  padding: 46px 20px;
  text-align: center;
  border: 1px dashed rgba(56, 189, 248, .2);
  border-radius: 14px;
  color: #5b7295;
}

.tx-empty i {
  font-size: 40px;
  color: rgba(34, 211, 238, .5);
  margin-bottom: 10px;
  display: block;
}

.tx-empty p {
  margin: 0;
  font-size: 13px;
  letter-spacing: 1px;
}

/* ================= X 确认弹窗 ================= */
.tx-dialog .el-dialog {
  background: rgba(13, 20, 36, .96) !important;
  border: 1px solid rgba(192, 132, 252, .35) !important;
  border-radius: 14px;
  backdrop-filter: blur(14px);
}

.tx-dialog .el-dialog__title {
  color: #e2f5ff;
  letter-spacing: 2px;
}

.tx-x-confirm {
  text-align: center;
  padding: 6px 4px;
}

.tx-x-icon {
  font-size: 40px;
  color: #c084fc;
  text-shadow: 0 0 18px rgba(192, 132, 252, .7);
  margin-bottom: 10px;
  display: block;
}

.tx-x-text {
  color: #e2f5ff;
  font-size: 15px;
  line-height: 1.7;
  margin: 0 0 8px;
}

.tx-x-sub {
  color: #5b7295;
  font-size: 13px;
  margin: 0;
}

.tx-x-sub b {
  color: #22d3ee;
}

/* ================= 响应式 ================= */
@media (max-width: 768px) {
  .tx-wrap {
    padding: 24px 14px 48px;
  }

  .tx-hero-title {
    font-size: 26px;
    letter-spacing: 3px;
  }

  .tx-upload .el-upload-dragger {
    height: 150px;
  }

  .tx-panel {
    padding: 18px;
  }

  .tx-col {
    width: 100%;
  }
}
.tx-note {
  margin-left: 6px;
  color: #e6a23c;
  cursor: help;
}

.tx-privacy {
    margin: 6px 0 0;
    color: #5b7295;
    font-size: 12px;
    letter-spacing: .5px;
}
.tx-privacy i { color: #34d399; margin-right: 4px; }

</style>

<script>
import CommonLayout from './common/commonPage.vue';
import { detectFile, getCapability, createTask, getTask, downloadAndSave } from '../api/conversion';
import { normalizeError } from '../api/request';
import { addTask, taskStore, RETENTION_HOURS, setOcrHandoff } from '../store';
import { progressLine, ensureNotifyPermission, notifyTaskDone, titleHint } from '../utils/task';
import { BASE_URL } from '../api/request';
import {
  MAX_UPLOAD_COUNT,
  UPLOAD_LIMIT_TIP,
  checkFileSize,
  checkFileList,
} from '../utils/upload-limit';

export default {
  props: {
    selectedNav: {
      type: String,
      default: 'home'
    }
  },
  components: {
    CommonLayout,
  },
  data() {
    return {
      uploadUrl: BASE_URL + '/api/files/upload/multiple',
      fileListData: [], // { uid, name(存储名), originalName, size, raw, detect }
      converting: false,
      capLoading: false,
      formatValue: '',
      capability: [], // [{target, loss, lossLabel, lossHint, warn, ...}]
      unsupportedTip: '',
      detectTips: [],
      xConfirmVisible: false,
      xConfirmText: '',
      pollTimers: {},
      // 上传限制来自 SSOT（src/utils/upload-limit.js），不要在模板里写死数字
      maxUploadCount: MAX_UPLOAD_COUNT,
      uploadLimitTip: UPLOAD_LIMIT_TIP,
    };
  },
  computed: {
    // 源格式：优先使用内容识别的有效扩展名
    sourceExt() {
      if (!this.fileListData.length) return '';
      const first = this.fileListData[0];
      if (first.detect && first.detect.effective) return first.detect.effective;
      const on = first.originalName || '';
      const parts = on.split('.');
      return parts.length > 1 ? parts.pop().toLowerCase() : '';
    },
    options() {
      return this.capability.map((c) => ({
        value: c.target,
        label: c.target,
        loss: c.loss,
        lossLabel: c.lossLabel || c.loss,
        lossHint: c.lossHint || '',
        warn: !!c.warn,
      }));
    },
    resultTasks() {
      // 仅展示有进度信息的任务（来自共享 store）
      return taskStore.tasks.filter((t) => t.id || t.status !== 'queued' || t.source);
    },
  },
  methods: {
    handleRemove(file) {
      this.fileListData = this.fileListData.filter((f) => f.uid !== file.uid);
      this.afterFilesChanged();
    },
    handlePreview(file) {
      console.log(file);
    },
    handleExceed() {
      this.$message.warning(`最多只能上传 ${MAX_UPLOAD_COUNT} 个文件，请先移除部分文件再添加`);
    },
    /**
     * 单文件大小校验（选中即刻拦截，避免用户白等整个文件传完才收到后端 413/400）
     * 返回 false 时 el-upload 不会发起这次上传。
     */
    beforeUpload(file) {
      const err = checkFileSize(file);
      if (err) {
        this.$message.error(err);
        return false;
      }
      return true;
    },
    beforeRemove(file) {
      return this.$confirm(`确定移除 ${file.name}？`);
    },
    // 上传成功后：保存文件信息并触发格式识别
    async uploadSuccess(response, file) {
      const entry = {
        uid: file.uid,
        name: response.data, // 后端随机命名
        originalName: file.name, // 原名（用于展示 / 下载命名）
        size: file.size,
        raw: file.raw,
        detect: null,
      };
      this.fileListData.push(entry);
      // 内容识别
      try {
        const det = await detectFile(file.raw);
        entry.detect = det.data;
        this.collectDetectTips(entry);
      } catch (e) {
        const info = normalizeError(e);
        this.$message.warning('格式识别失败：' + info.message);
      }
      this.afterFilesChanged();
    },
    collectDetectTips(entry) {
      const d = entry.detect;
      if (!d) return;
      if (d.confident === false) {
        const tip = (d.hints && d.hints.join('；')) ||
          `扩展名「${d.extByExtension}」与内容实际格式「${d.extByContent}」不一致，已按内容识别为准`;
        this.detectTips.push(tip);
      }
      // 扫描件：/api/detect 的 scan 段（与后端 readers/pdf 同一套判定口径）
      if (d.scan && d.scan.needOcr) {
        this.detectTips.push(
          `「${entry.originalName}」为扫描件（无文字层），转换只能得到图片版；` +
          `需要可编辑文字请改用 OCR 识别`
        );
      }
    },
    afterFilesChanged() {
      this.formatValue = '';
      this.capability = [];
      this.unsupportedTip = '';
      this._scanConfirmed = false;      // 文件变了 → 扫描件提醒重新生效
      if (this.fileListData.length === 0) {
        this.detectTips = [];
        return;
      }
      this.refreshCapability();
    },
    // 拉取能力矩阵，渲染目标下拉
    async refreshCapability() {
      const source = this.sourceExt;
      if (!source) return;
      this.capLoading = true;
      try {
        const resp = await getCapability(source);
        this.capability = resp.data || [];
        if (this.capability.length === 0) {
          this.unsupportedTip = `格式「${source}」暂不支持转换，已列入二期规划`;
        } else {
          this.unsupportedTip = '';
        }
      } catch (e) {
        const info = normalizeError(e);
        this.capability = [];
        this.unsupportedTip = info.message;
      } finally {
        this.capLoading = false;
      }
    },
    onTargetChange() {
      // 切换目标时清空可能的 X 确认状态
      // 扫描件提示也跟着重置：换了目标格式（比如改成 PNG）就不该再拦住用户
      this._scanConfirmed = false;
    },
    // 点击转换：X 类需二次确认；扫描件先问一次要不要改用 OCR
    async onConvertClick() {
      if (this.fileListData.length === 0) {
        this.$message.warning('请先上传文件');
        return;
      }
      if (!this.formatValue) {
        this.$message.warning('请选择目标格式');
        return;
      }
      // 校验同一源格式
      const sources = this.fileListData.map((f) => f.detect && f.detect.effective ? f.detect.effective : this.extOf(f.originalName));
      const uniq = [...new Set(sources)];
      if (uniq.length > 1) {
        this.$message.warning('一次转换需为同一种源格式');
        return;
      }
      // 扫描件前置提醒：直接转换只能得到图片版，先问用户要不要改用 OCR。
      // 用户点「继续转换」后本批不再重复打扰（_scanConfirmed）。
      if (!this._scanConfirmed && this.needsTextOutput()) {
        const scan = this.firstScannedEntry();
        if (scan) {
          const jumped = await this.askScanOcr(scan);
          if (jumped) return;
          this._scanConfirmed = true;
        }
      }
      const opt = this.options.find((o) => o.value === this.formatValue);
      if (opt && (opt.loss === 'X' || opt.warn)) {
        this.xConfirmText = opt.lossHint ||
          (opt.lossLabel ? `${opt.lossLabel}，请核对后再转换` : '该转换存在信息损失，请确认后继续');
        this.xConfirmVisible = true;
        return;
      }
      this.doConvert();
    },
    /** 目标是否承载文字：转图片（png/jpg…）时扫描件照转即可，不必提示 OCR */
    needsTextOutput() {
      const IMAGE_TARGETS = ['png', 'jpg', 'jpeg', 'jfif', 'bmp', 'gif', 'tif', 'tiff', 'webp', 'ico'];
      return !IMAGE_TARGETS.includes(String(this.formatValue || '').toLowerCase());
    },
    /** 本批第一个扫描件（有文字层缺失、转换后拿不到可编辑文字） */
    firstScannedEntry() {
      return this.fileListData.find(
        (f) => f.detect && f.detect.scan && f.detect.scan.needOcr
      ) || null;
    },
    /**
     * 弹「检测到扫描件，是否改用 OCR 识别」。
     * 返回 true = 已跳转 OCR 页；false = 用户选择继续做格式转换。
     */
    askScanOcr(entry) {
      // 没拿到存储名（上传响应异常）就别拦：照常走转换，不能让提示变成死路
      if (!entry || !entry.name) return Promise.resolve(false);
      const scans = this.fileListData.filter(
        (f) => f.detect && f.detect.scan && f.detect.scan.needOcr
      );
      const more = scans.length > 1
        ? `\n本次共 ${scans.length} 个扫描件，已先带上「${entry.originalName}」；` +
          `其余文件可在识别完成后再提交。`
        : '';
      return this.$confirm(
        `「${entry.originalName}」没有文字层（扫描件），直接转换只能得到图片版，` +
        `内容不可编辑，也无法搜索。\n改用 OCR 识别可以提取可编辑文字 —— ` +
        `该文件已上传完成，无需重新选择。${more}`,
        '检测到扫描件',
        {
          confirmButtonText: '去 OCR 识别',
          cancelButtonText: '继续转换',
          type: 'warning',
          distinguishCancelAndClose: true,
        }
      ).then(() => {
        // 交接已上传的存储名：OCR 页按 fileId 提交，不再重复上传
        setOcrHandoff({
          fileId: entry.name,
          name: entry.originalName,
          size: entry.size,
          // 带上用户原本选的目标格式（OCR 页支持时直接预选，省一步）
          target: this.formatValue,
        });
        this.$router.push({ name: 'ocrPage' });
        return true;
      }).catch(() => false);
    },
    confirmConvert() {
      this.xConfirmVisible = false;
      this.doConvert();
    },
    extOf(name) {
      const parts = (name || '').split('.');
      return parts.length > 1 ? parts.pop().toLowerCase() : '';
    },
    async doConvert() {
      // 提交前兜底校验：选中文件时已拦过一次，这里再兜一次，
      // 让「单文件不超过 10M · 最多 5 个」在任何入口下都真正成立。
      const limitErr = checkFileList(
        this.fileListData.map((f) => ({ name: f.originalName, size: f.size }))
      );
      if (limitErr) {
        this.$message.error(limitErr);
        return;
      }
      this.converting = true;
      const source = this.sourceExt;
      const target = this.formatValue;
      try {
        for (const f of this.fileListData) {
          const task = {
            id: null,
            fileName: f.originalName,
            source,
            target,
            status: 'queued',
            progress: 0,
            // 页进度 / 剩余时间由后端下发（OCR 任务会填，普通转换恒为 0/0）
            pagesDone: 0,
            pagesTotal: 0,
            etaSeconds: null,
            result: null,
            error: null,
            hint: null,
            createdAt: Date.now(),
            retentionHours: RETENTION_HOURS,
          };
          addTask(task);
          try {
            // 桌面通知授权：在用户点击「开始转换」的上下文里请求才弹得出来。
            // 拒绝也不影响任务本身。
            ensureNotifyPermission();
            const resp = await createTask({ file: f.raw, source, target, async: true });
            task.id = resp.data.taskId;
            task.status = resp.data.status || 'running';
            this.pollTask(task);
          } catch (e) {
            const info = normalizeError(e);
            task.status = 'failed';
            task.error = info.message;
            task.hint = info.hint;
            this.$message.error('任务创建失败：' + info.message);
          }
        }
      } finally {
        this.converting = false;
      }
    },
    /** 运行中任务的页进度 / 剩余时间文案（只读后端实测字段，估不出不显示） */
    etaOf(row) {
      return progressLine(row);
    },
    /**
     * 任务到达终态：标题提示 + 桌面通知。
     * 转换任务通常几秒到一两分钟，用户往往还守着页面 —— 所以
     * `notifyTaskDone` 内部只在**页面处于后台**时才弹，前台只给页面内提示。
     */
    onTaskDone(task, ok) {
      titleHint(ok ? '✓ 转换完成' : '✕ 转换失败');
      notifyTaskDone(
        ok ? '文件转换完成' : '文件转换失败',
        `「${task.fileName}」${ok ? '已处理完成，可下载' : '处理失败，请查看任务中心'}`
      );
      clearTimeout(this._titleTimer);
      this._titleTimer = setTimeout(() => {
        const running = this.tasks.some((t) => t.status !== 'success' && t.status !== 'failed');
        titleHint(running ? '转换中' : '');
      }, 8000);
    },
    pollTask(task) {
      if (!task.id) return;
      getTask(task.id)
        .then((resp) => {
          const d = resp.data;
          task.status = d.status;
          task.progress = typeof d.progress === 'number' ? d.progress : task.progress;
          if (d.result) task.result = d.result;
          if (d.error) task.error = typeof d.error === 'string' ? d.error : JSON.stringify(d.error);
            if (d.note) task.note = d.note;
          // 页进度与实测剩余时间（后端下发；缺字段时保持上一次的值）
          if (typeof d.pagesDone === 'number') task.pagesDone = d.pagesDone;
          if (typeof d.pagesTotal === 'number') task.pagesTotal = d.pagesTotal;
          if (d.etaSeconds !== undefined) {
            task.etaSeconds = (d.etaSeconds === null ? null : Number(d.etaSeconds));
          }
          if (d.status === 'success' || d.status === 'failed') {
            if (d.status === 'success') {
              this.$message.success(`「${task.fileName}」转换完成`);
            } else {
              this.$message.error(`「${task.fileName}」转换失败`);
            }
            this.onTaskDone(task, d.status === 'success');
            return;
          }
          this.schedulePoll(task);
        })
        .catch(() => {
          // 网络抖动后继续轮询，直到终态
          if (task.status !== 'success' && task.status !== 'failed') {
            this.schedulePoll(task);
          }
        });
    },
    schedulePoll(task) {
      clearTimeout(this.pollTimers[task.id]);
      this.pollTimers[task.id] = setTimeout(() => this.pollTask(task), 1200);
    },
    statusText(status) {
      return { queued: '排队中', running: '转换中', success: '完成', failed: '失败' }[status] || status;
    },
    progressStatus(row) {
      if (row.status === 'success') return 'success';
      if (row.status === 'failed') return 'exception';
      return null;
    },
    // T-8：下载使用原名 + 目标扩展名，避免乱码
    downloadResult(row) {
      if (!row.result) return;
      const base = (row.fileName || 'file').replace(/\.[^.]+$/, '');
      const name = `${base}.${row.target}`;
      this.$message.info('开始下载，大文件可能需要一些时间…');
      downloadAndSave(row.result, name)
        .then(() => this.$message.success('下载完成'))
        .catch(() => {
          this.$message.error('下载失败，请确认后端在线');
        });
    },
    retryTask(row) {
      const source = row.source;
      const target = row.target;
      const f = this.fileListData.find((x) => x.originalName === row.fileName);
      if (!f || !f.raw) {
        this.$message.warning('原始文件已移除，无法重试');
        return;
      }
      row.status = 'queued';
      row.progress = 0;
      row.error = null;
      row.hint = null;
      createTask({ file: f.raw, source, target, async: true })
        .then((resp) => {
          row.id = resp.data.taskId;
          row.status = resp.data.status || 'running';
          this.pollTask(row);
        })
        .catch((e) => {
          const info = normalizeError(e);
          row.status = 'failed';
          row.error = info.message;
        });
    },
  },
  beforeDestroy() {
    Object.values(this.pollTimers).forEach(clearTimeout);
  },
}
</script>
